/**
 * ExistingVideosManager.jsx
 *
 * ضيف الـ import ده في ProductsPage.jsx:
 *   import ExistingVideosManager from "./ExistingVideosManager";
 *
 * واستخدمه جوه تاب الصور والفيديو في ProductModal:
 *   {isEdit && <ExistingVideosManager productId={editRecord?.id} />}
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { getProduct, deleteProductVideo } from "../../api/productsApi";

const { Text } = Typography;

export default function ExistingVideosManager({ productId, onVideosChange }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // ── جلب الفيديوهات الموجودة ──────────────────────────────────────────────
  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    getProduct(productId)
      .then(({ data }) => {
        const vids = data?.data?.videos ?? data?.videos ?? [];
        setVideos(vids);
        onVideosChange?.(vids.length);
      })
      .catch(() => message.error("فشل تحميل فيديوهات المنتج"))
      .finally(() => setLoading(false));
  }, [productId]);

  // ── حذف فيديو ────────────────────────────────────────────────────────────
  const handleDelete = async (videoId) => {
    setDeletingId(videoId);
    try {
      await deleteProductVideo(productId, videoId);
      const updated = videos.filter((v) => v.id !== videoId);
      setVideos(updated);
      onVideosChange?.(updated.length);
      message.success("تم حذف الفيديو");
    } catch {
      message.error("فشل حذف الفيديو");
    } finally {
      setDeletingId(null);
    }
  };

  // ── لا يوجد فيديوهات ────────────────────────────────────────────────────
  if (!loading && videos.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "20px 0 8px",
          color: "#94A3B8",
          fontSize: 13,
        }}
      >
        <VideoCameraOutlined
          style={{ fontSize: 28, display: "block", marginBottom: 6 }}
        />
        لا توجد فيديوهات مضافة بعد
      </div>
    );
  }

  // ── العرض ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        background: "#FAFAFA",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        padding: "14px 16px",
        marginBottom: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <Space size={6}>
          <VideoCameraOutlined style={{ color: "#6366F1" }} />
          <Text style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>
            الفيديوهات الحالية
          </Text>
          <Tag
            style={{
              borderRadius: 20,
              fontSize: 11,
              background: "#EEF2FF",
              border: "none",
              color: "#6366F1",
              fontWeight: 600,
            }}
          >
            {videos.length}
          </Tag>
        </Space>
        {loading && <Spin size="small" />}
      </div>

      {/* Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 10,
        }}
      >
        {videos.map((v) => {
          const isDeleting = deletingId === v.id;
          return (
            <div
              key={v.id}
              style={{
                position: "relative",
                borderRadius: 10,
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                background: "#000",
                opacity: isDeleting ? 0.4 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <video
                src={v.video || v.url}
                controls
                style={{ width: "100%", height: 110, display: "block" }}
              />

              {/* Actions Bar */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 4,
                  padding: "4px 6px",
                  background: "#F8FAFC",
                }}
              >
                <Popconfirm
                  title="حذف الفيديو؟"
                  description="لن تتمكن من استرجاعه"
                  onConfirm={() => handleDelete(v.id)}
                  okText="حذف"
                  cancelText="إلغاء"
                  okType="danger"
                  placement="top"
                >
                  <Tooltip title="حذف">
                    <Button
                      type="text"
                      size="small"
                      loading={isDeleting}
                      icon={
                        <DeleteOutlined
                          style={{ color: "#F87171", fontSize: 13 }}
                        />
                      }
                      style={{ padding: "0 4px", height: 22 }}
                    />
                  </Tooltip>
                </Popconfirm>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}